import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import CategoryBadge from "../shared/components/CategoryBadge";

export default function QcFlagsTab({ showToast }) {
  const navigate = useNavigate();

  // ── Own Firebase subscription — only mounts when this tab is active ───────
  const [qcFlagIndex, setQcFlagIndex] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(true);
  const [filterStatus, setFilterStatus] = useState("flagged");

  // ── Active QC audits — every order doc across every QC member's
  // qualityReports, regardless of visit/cleared status. An order is
  // "active" simply by existing here; it drops off once deleted. ─────────
  const [activeAudits, setActiveAudits] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(true);

  // ── uid → name lookup, used to label the QC Member column on both
  // tables without needing Quality.jsx to denormalize a name field
  // onto every order doc. ──────────────────────────────────────────────────
  const [usersMap, setUsersMap] = useState({});

  // ── Shared delete-confirm key for both tables' two-step delete UI ──────
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, "qcFlagIndex"),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setQcFlagIndex(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingFlags(false);
      },
      (err) => {
        console.error("Failed to fetch qcFlagIndex:", err);
        setLoadingFlags(false);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(
      collectionGroup(db, "qualityReports"),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setActiveAudits(
          snap.docs.map((d) => ({
            id: d.id,
            ownerUid: d.ref.parent.parent.id,
            ...d.data(),
          })),
        );
        setLoadingAudits(false);
      },
      (err) => {
        console.error("Failed to fetch active QC audits:", err);
        setLoadingAudits(false);
      },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = d.data()?.name || d.id;
        });
        setUsersMap(map);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    })();
  }, []);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const flags = qcFlagIndex.filter((f) => f.type !== "delete_request");
    const deleteRequests = qcFlagIndex.filter(
      (f) => f.type === "delete_request",
    );
    return {
      total: qcFlagIndex.length,
      pending: flags.filter((f) => f.status === "flagged").length,
      pendingDeletes: deleteRequests.filter((f) => f.status === "pending")
        .length,
      approved: qcFlagIndex.filter((f) => f.status === "approved").length,
      rejected: qcFlagIndex.filter((f) => f.status === "rejected").length,
      members: new Set(qcFlagIndex.map((f) => f.ownerUid)).size,
    };
  }, [qcFlagIndex]);

  const filteredFlags = useMemo(() => {
    switch (filterStatus) {
      case "all":
        return qcFlagIndex;
      case "flagged":
        return qcFlagIndex.filter(
          (f) => f.type !== "delete_request" && f.status === "flagged",
        );
      case "delete_request":
        return qcFlagIndex.filter(
          (f) => f.type === "delete_request" && f.status === "pending",
        );
      case "approved":
        return qcFlagIndex.filter((f) => f.status === "approved");
      case "rejected":
        return qcFlagIndex.filter((f) => f.status === "rejected");
      default:
        return qcFlagIndex;
    }
  }, [qcFlagIndex, filterStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleApprove = useCallback(
    async (flag) => {
      if (flag.type === "delete_request") {
        try {
          if (flag.scope === "order") {
            const visitsSnap = await getDocs(
              collection(
                db,
                "users",
                flag.ownerUid,
                "qualityReports",
                flag.orderId,
                "visits",
              ),
            );
            await Promise.all(
              visitsSnap.docs.map((d) =>
                deleteDoc(
                  doc(
                    db,
                    "users",
                    flag.ownerUid,
                    "qualityReports",
                    flag.orderId,
                    "visits",
                    d.id,
                  ),
                ),
              ),
            );
            await deleteDoc(
              doc(db, "users", flag.ownerUid, "qualityReports", flag.orderId),
            );
          } else {
            await deleteDoc(
              doc(
                db,
                "users",
                flag.ownerUid,
                "qualityReports",
                flag.orderId,
                "visits",
                flag.visitId,
              ),
            );
          }
          await updateDoc(doc(db, "qcFlagIndex", flag.id), {
            status: "approved",
          });
          showToast(
            flag.scope === "order"
              ? `Order "${flag.orderId}" and its visits deleted.`
              : `Visit "${flag.milestone}" deleted.`,
            "error",
          );
        } catch (err) {
          showToast(err.message, "error");
        }
        return;
      }

      await updateDoc(doc(db, "qcFlagIndex", flag.id), { status: "approved" });
      await updateDoc(
        doc(
          db,
          "users",
          flag.ownerUid,
          "qualityReports",
          flag.orderId,
          "visits",
          flag.visitId,
        ),
        { flagStatus: "approved", flagged: false },
      );
      showToast(`Flag on "${flag.milestone}" approved.`);
    },
    [showToast],
  );

  const handleReject = useCallback(
    async (flag) => {
      if (flag.type === "delete_request") {
        try {
          const targetRef =
            flag.scope === "order"
              ? doc(db, "users", flag.ownerUid, "qualityReports", flag.orderId)
              : doc(
                  db,
                  "users",
                  flag.ownerUid,
                  "qualityReports",
                  flag.orderId,
                  "visits",
                  flag.visitId,
                );
          await Promise.all([
            updateDoc(doc(db, "qcFlagIndex", flag.id), { status: "rejected" }),
            updateDoc(targetRef, { deleteRequested: false }),
          ]);
          showToast(
            flag.scope === "order"
              ? `Delete request for order "${flag.orderId}" dismissed — order stays active.`
              : `Delete request for "${flag.milestone}" dismissed — visit stays active.`,
            "error",
          );
        } catch (err) {
          showToast(err.message, "error");
        }
        return;
      }

      await updateDoc(doc(db, "qcFlagIndex", flag.id), { status: "rejected" });
      await updateDoc(
        doc(
          db,
          "users",
          flag.ownerUid,
          "qualityReports",
          flag.orderId,
          "visits",
          flag.visitId,
        ),
        { flagStatus: "rejected" },
      );
      showToast(`Flag on "${flag.milestone}" rejected.`, "error");
    },
    [showToast],
  );

  // Cleanup-only delete: removes a resolved row from qcFlagIndex. Doesn't
  // touch the underlying order/visit — Approve/Reject already settled that.
  const handleDeleteFlagEntry = useCallback(
    async (flag) => {
      const key = `flag:${flag.id}`;
      if (deleteConfirm !== key) {
        setDeleteConfirm(key);
        return;
      }
      try {
        await deleteDoc(doc(db, "qcFlagIndex", flag.id));
        setDeleteConfirm(null);
        showToast("Entry removed.");
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [deleteConfirm, showToast],
  );

  // Real cascade delete: removes the order doc + all its visits from that
  // QC member's qualityReports. Admin-only tab, so this is immediate (no
  // request/approval step) — same end result as the admin branch of
  // handleDeleteOrder in Quality.jsx, just reachable cross-member.
  const handleDeleteActiveAudit = useCallback(
    async (audit) => {
      const key = `audit:${audit.ownerUid}:${audit.id}`;
      if (deleteConfirm !== key) {
        setDeleteConfirm(key);
        return;
      }
      try {
        const visitsSnap = await getDocs(
          collection(
            db,
            "users",
            audit.ownerUid,
            "qualityReports",
            audit.id,
            "visits",
          ),
        );
        await Promise.all(
          visitsSnap.docs.map((d) =>
            deleteDoc(
              doc(
                db,
                "users",
                audit.ownerUid,
                "qualityReports",
                audit.id,
                "visits",
                d.id,
              ),
            ),
          ),
        );
        await deleteDoc(
          doc(db, "users", audit.ownerUid, "qualityReports", audit.id),
        );
        setDeleteConfirm(null);
        showToast(
          `Order "${audit.orderId || audit.id}" and its visits deleted.`,
          "error",
        );
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [deleteConfirm, showToast],
  );

  return (
    <>
      <h2 className={styles.sectionTitle}>QC Flags Overview</h2>
      <div className={styles.statsRow}>
        <StatCard
          label="Total Flagged"
          value={stats.total}
          sub="across all QC members"
        />
        <StatCard
          label="Pending Review"
          value={stats.pending}
          sub="awaiting admin action"
        />
        <StatCard
          label="Pending Deletes"
          value={stats.pendingDeletes}
          sub="delete requests awaiting approval"
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          sub="acknowledged by admin"
        />
        <StatCard
          label="Rejected"
          value={stats.rejected}
          sub="step under review"
        />
        <StatCard
          label="QC Members"
          value={stats.members || "—"}
          sub="members with active flags"
        />
      </div>

      <div className={styles.rule} />

      <h2 className={styles.sectionTitle}>QC Flag Reviews</h2>
      {loadingFlags ? (
        <p className={styles.stateText}>Loading flags…</p>
      ) : (
        <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
          <div className={styles.tableControls}>
            <div className={styles.tableFilters}>
              <div className={styles.tableFilterGroup}>
                <span className={styles.tableFilterLabel}>Status</span>
                <SelectWrapper
                  value={filterStatus}
                  selectClassName={styles.tableFilterSelect}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="flagged">Flagged</option>
                  <option value="delete_request">Delete Requests</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </SelectWrapper>
              </div>
            </div>
          </div>

          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.th}>#</th>
                  <th className={styles.th}>Order ID</th>
                  <th className={styles.th}>Milestone</th>
                  <th className={styles.th}>Concern</th>
                  <th className={styles.th}>QC Member</th>
                  <th className={styles.th}>Date</th>
                  <th className={styles.th}>Documents</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>View</th>
                  <th className={styles.th}>Actions</th>
                  <th className={styles.th}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {filteredFlags.map((flag, index) => (
                  <tr key={flag.id} className={styles.tableRow}>
                    <td className={styles.td}>{index + 1}</td>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {flag.orderId || "—"}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.milestoneBadge}>
                        {flag.milestone ||
                          (flag.scope === "order" ? "Whole Order" : "—")}
                      </span>
                    </td>
                    <td className={`${styles.td} ${styles.subTdWrap}`}>
                      {flag.type === "delete_request" ? (
                        <span style={{ opacity: 0.6, fontSize: 10 }}>
                          {flag.scope === "order"
                            ? "Requesting deletion of this order & all its visits"
                            : "Requesting deletion of this visit"}
                        </span>
                      ) : flag.concern?.trim() ? (
                        <span
                          style={{
                            display: "inline-block",
                            background: "rgba(220,100,100,0.10)",
                            border: "1px solid rgba(220,100,100,0.22)",
                            borderRadius: 5,
                            padding: "3px 8px",
                            fontSize: 10,
                            color: "#e08080",
                          }}
                        >
                          {flag.concern}
                        </span>
                      ) : (
                        <span style={{ opacity: 0.35 }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.supplierLink}
                        onClick={() => navigate(`/quality/${flag.ownerUid}`)}
                      >
                        {flag.ownerName}
                      </button>
                    </td>
                    <td className={styles.td} style={{ whiteSpace: "nowrap" }}>
                      {flag.date
                        ? (flag.date.toDate?.().toLocaleDateString("en-GB") ??
                          flag.date)
                        : flag.requestedAt
                          ? (flag.requestedAt
                              .toDate?.()
                              .toLocaleDateString("en-GB") ?? "—")
                          : "—"}
                    </td>
                    <td className={styles.td}>
                      {flag.documents?.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {flag.documents.map((d, i) => (
                            <a
                              key={i}
                              href={d}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                fontSize: 10,
                                color: "var(--gold)",
                                textDecoration: "none",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ↗ {d.split("/").pop()}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span style={{ opacity: 0.35 }}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <CategoryBadge
                        category={
                          flag.type === "delete_request" &&
                          flag.status === "pending"
                            ? "flagged"
                            : flag.status
                        }
                        label={
                          flag.type === "delete_request"
                            ? flag.status === "pending"
                              ? "🗑 Delete Request"
                              : flag.status === "approved"
                                ? "🗑 Deleted"
                                : "🗑 Request Dismissed"
                            : flag.status === "flagged"
                              ? "⚑ Flagged"
                              : undefined
                        }
                      />
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.editBtn}
                        onClick={() => navigate(`/quality/${flag.ownerUid}`)}
                        title="Open QC page"
                      >
                        ↗
                      </button>
                    </td>
                    <td className={styles.td}>
                      {(flag.status === "flagged" ||
                        flag.status === "pending") && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            style={{
                              padding: "5px 12px",
                              fontSize: 9,
                              marginTop: 0,
                            }}
                            onClick={() => handleApprove(flag)}
                          >
                            Approve
                          </button>
                          <button
                            className={styles.btn}
                            style={{
                              padding: "5px 12px",
                              fontSize: 9,
                              color: "#e08080",
                              borderColor: "rgba(220,100,100,0.3)",
                            }}
                            onClick={() => handleReject(flag)}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                    <td className={styles.td}>
                      {(flag.status === "approved" ||
                        flag.status === "rejected") &&
                        (deleteConfirm === `flag:${flag.id}` ? (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary}`}
                              style={{
                                padding: "4px 10px",
                                fontSize: 9,
                                marginTop: 0,
                                background: "#c84040",
                                borderColor: "#c84040",
                              }}
                              onClick={() => handleDeleteFlagEntry(flag)}
                            >
                              Confirm
                            </button>
                            <button
                              className={styles.btn}
                              style={{ padding: "4px 10px", fontSize: 9 }}
                              onClick={() => setDeleteConfirm(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.editBtn}
                            style={{ color: "#e08080" }}
                            onClick={() => handleDeleteFlagEntry(flag)}
                            title="Remove this entry"
                          >
                            ✕
                          </button>
                        ))}
                    </td>
                  </tr>
                ))}
                {filteredFlags.length === 0 && (
                  <tr>
                    <td
                      className={styles.td}
                      colSpan={11}
                      style={{ textAlign: "center", opacity: 0.5 }}
                    >
                      No flags found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={styles.rule} />

      <h2 className={styles.sectionTitle}>Active QC Audits</h2>
      {loadingAudits ? (
        <p className={styles.stateText}>Loading audits…</p>
      ) : (
        <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.th}>#</th>
                  <th className={styles.th}>Order ID</th>
                  <th className={styles.th}>Supplier</th>
                  <th className={styles.th}>Country</th>
                  <th className={styles.th}>City</th>
                  <th className={styles.th}>QC Member</th>
                  <th className={styles.th}>Created</th>
                  <th className={styles.th}>View</th>
                  <th className={styles.th}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {activeAudits.map((audit, index) => {
                  const auditKey = `audit:${audit.ownerUid}:${audit.id}`;
                  return (
                    <tr
                      key={`${audit.ownerUid}_${audit.id}`}
                      className={styles.tableRow}
                    >
                      <td className={styles.td}>{index + 1}</td>
                      <td
                        className={styles.td}
                        style={{ fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        {audit.orderId || audit.id}
                      </td>
                      <td className={styles.td}>{audit.supplier || "—"}</td>
                      <td className={styles.td}>
                        <span
                          className={`${styles.categoryBadge} ${styles.cat_woven}`}
                        >
                          {audit.country || "—"}
                        </span>
                      </td>
                      <td className={styles.td}>{audit.city || "—"}</td>
                      <td className={styles.td}>
                        <button
                          className={styles.supplierLink}
                          onClick={() => navigate(`/quality/${audit.ownerUid}`)}
                        >
                          {usersMap[audit.ownerUid] || audit.ownerUid}
                        </button>
                      </td>
                      <td
                        className={styles.td}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {audit.createdAt
                          ?.toDate?.()
                          .toLocaleDateString("en-GB") ?? "—"}
                      </td>
                      <td className={styles.td}>
                        <button
                          className={styles.editBtn}
                          onClick={() => navigate(`/quality/${audit.ownerUid}`)}
                          title="Open QC page"
                        >
                          ↗
                        </button>
                      </td>
                      <td className={styles.td}>
                        {deleteConfirm === auditKey ? (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary}`}
                              style={{
                                padding: "4px 10px",
                                fontSize: 9,
                                marginTop: 0,
                                background: "#c84040",
                                borderColor: "#c84040",
                              }}
                              onClick={() => handleDeleteActiveAudit(audit)}
                            >
                              Confirm
                            </button>
                            <button
                              className={styles.btn}
                              style={{ padding: "4px 10px", fontSize: 9 }}
                              onClick={() => setDeleteConfirm(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.editBtn}
                            style={{ color: "#e08080" }}
                            onClick={() => handleDeleteActiveAudit(audit)}
                            title="Delete order & all its visits"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {activeAudits.length === 0 && (
                  <tr>
                    <td
                      className={styles.td}
                      colSpan={9}
                      style={{ textAlign: "center", opacity: 0.5 }}
                    >
                      No active QC audits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={styles.rule} />
    </>
  );
}
